import { memo, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AiOutlineClose, AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';
import { TermSelect } from './fields';
import { FIELD_PLACEHOLDERS, STORAGE_LENGTH_COPY } from './constants';
import { addOnStart, exceedsTerm, heldLengthLabel, termLabel, untilSubscriptionEndLabel, YEAR_TERMS } from './subscriptionTerms';
import { AddOnCategory, SpecialTerm } from '../../../../enums/clientEnums';
import type { ClientFormValues, SubscriptionTerm } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

interface AddOnRowProps {
  index: number;
  name: string;
  /** Modules are always one, so they show no counter. */
  category?: AddOnCategory;
  /** Null = unlimited. */
  maxQuantity: number | null;
  /** The count already saved (0 for a new row); an active add-on can't go below it. */
  heldQuantity: number;
  /** Only an expired add-on may be lowered or removed. */
  isExpired: boolean;
  /** The saved length and expiry; an active add-on can only be extended beyond them. */
  heldYears: number | null;
  expiresAt: string | null;
  /** False for storage, which has no length of its own and so shows no length control. */
  hasExpiry: boolean;
  /** The subscription's own term; no add-on may outlast it. */
  limit: SubscriptionTerm | '';
  currentExpiry?: string | null;
  onRemove: (index: number) => void;
  onOpenCategory: (category: AddOnCategory) => void;
}

const AddOnRow = ({
  index,
  name,
  category,
  maxQuantity,
  heldQuantity,
  isExpired,
  heldYears,
  expiresAt,
  hasExpiry,
  limit,
  currentExpiry,
  onRemove,
  onOpenCategory,
}: AddOnRowProps) => {
  const { control, setValue } = useFormContext<ClientFormValues>();
  const quantity = useWatch({ control, name: `addOns.${index}.quantity` });
  const renew = useWatch({ control, name: `addOns.${index}.renew` });
  const isModule = category === AddOnCategory.MODULES;

  // A saved add-on only goes down once expired, and then only as a renewal; storage never does.
  const isSaved = heldQuantity > 0;
  const isActiveSaved = isSaved && !isExpired;
  const minQuantity = isSaved && (!isExpired || !hasExpiry) ? heldQuantity : 1;
  const canRemove = !isActiveSaved;
  // An expired add-on's count only changes as part of a renewal, so it stays locked until a length is picked.
  const isRenewalPending = isExpired && !renew;

  // Demo locks every row to its two weeks. Otherwise every year is listed, those outlasting the subscription
  // disabled rather than hidden. An active saved row only offers years above its own, counted from its own start.
  const options = useMemo(() => {
    if (limit === SpecialTerm.DEMO) return [{ value: SpecialTerm.DEMO, label: termLabel(SpecialTerm.DEMO) }];

    const from = isActiveSaved ? (addOnStart(expiresAt, heldYears) ?? undefined) : undefined;
    // With no stored length the row's start is unknown, so only "until subscription ends" is safe to offer.
    const years = YEAR_TERMS.filter((term) => !isActiveSaved || (heldYears !== null && Number(term) > heldYears));
    // Redundant for a new row under a whole-year limit; offered for a Current limit and for an active saved row, whose year steps count from its own start.
    const untilEnd = limit === SpecialTerm.CURRENT || isActiveSaved ? [{ value: SpecialTerm.CURRENT, label: untilSubscriptionEndLabel(limit, currentExpiry) }] : [];
    return [
      ...untilEnd,
      ...years.map((term) => ({ value: term, label: termLabel(term, undefined, from), disabled: exceedsTerm(term, limit, currentExpiry, from) })),
    ];
  }, [currentExpiry, expiresAt, heldYears, isActiveSaved, limit]);

  // A saved row shows what it holds until a new length is picked; an expired one asks to be renewed.
  const lengthPlaceholder = isExpired ? FIELD_PLACEHOLDERS.RENEW_ADD_ON : isSaved ? heldLengthLabel(heldYears, expiresAt) : FIELD_PLACEHOLDERS.ADD_ON_LENGTH;

  const handleRenewTerm = useCallback(
    (value: string) => {
      setValue(`addOns.${index}.term`, value as SubscriptionTerm, { shouldDirty: true });
      setValue(`addOns.${index}.renew`, true, { shouldDirty: true });
    },
    [index, setValue],
  );

  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);

  const handleOpenCategory = useCallback(() => {
    if (category) onOpenCategory(category);
  }, [category, onOpenCategory]);

  const handleDecrement = useCallback(() => {
    setValue(`addOns.${index}.quantity`, Math.max(minQuantity, quantity - 1), { shouldDirty: true });
  }, [index, minQuantity, quantity, setValue]);

  const handleIncrement = useCallback(() => {
    setValue(`addOns.${index}.quantity`, maxQuantity === null ? quantity + 1 : Math.min(maxQuantity, quantity + 1), { shouldDirty: true });
  }, [index, maxQuantity, quantity, setValue]);

  return (
    <div className={styles.addOnRow}>
      <button type="button" className={styles.addOnName} onClick={handleOpenCategory}>
        {name}
      </button>
      {isModule ? (
        <span />
      ) : (
        <div className={styles.addOnCounter}>
          <button
            type="button"
            className={styles.addOnCounterButton}
            aria-label={`Decrease ${name}`}
            disabled={isRenewalPending || quantity <= minQuantity}
            onClick={handleDecrement}
          >
            <AiOutlineMinus />
          </button>
          <span className={styles.addOnCounterValue}>{quantity}</span>
          <button
            type="button"
            className={styles.addOnCounterButton}
            aria-label={`Increase ${name}`}
            disabled={isRenewalPending || (maxQuantity !== null && quantity >= maxQuantity)}
            onClick={handleIncrement}
          >
            <AiOutlinePlus />
          </button>
        </div>
      )}
      {hasExpiry ? (
        <TermSelect name={`addOns.${index}.term`} options={options} placeholder={lengthPlaceholder} onSelected={isExpired ? handleRenewTerm : undefined} />
      ) : (
        <span className={styles.addOnNoLength}>{STORAGE_LENGTH_COPY}</span>
      )}
      {canRemove ? (
        <button type="button" className={styles.removeAddOn} aria-label={`Remove ${name}`} onClick={handleRemove}>
          <AiOutlineClose />
        </button>
      ) : (
        <span />
      )}
    </div>
  );
};

// Adding a row must not re-render the rows already listed.
export default memo(AddOnRow);
